import React, { useState, useEffect } from "react";
import { Col, Modal, ModalProps, Row } from "react-bootstrap";
import { Minus, Plus, Search } from "react-feather";
import { formatPrice } from "../constants";
import { AddCart } from "../compoment/icon";
import Close from "../compoment/icon/Close";
import AddtoCart from "./AddtoCart";
import { useDispatch, useSelector } from "react-redux";
import { upsertCartItem } from "../redux/slices/cartSlice";
import {
  normalizeMediaUrl,
  Product,
  resolveProductDisplayImage,
  resolveProductColorImage,
  productNameIncludesSticker,
  uniqueSortedProductSizes,
  sortProductColorsLightFirst,
  findPreferredProductColor,
  findPreferredProductSize,
  getProductSizeLabel,
  getProductColorLabel,
  formatProductSizeWithInches,
  getProductSizeInches,
  getAvailableStock,
  isVariantInStock,
  isSizeAvailableForColor,
  isColorAvailableForSize,
} from "../services/productService";
import PlaceholderImg from "../assets/img/e-commerce/placeholder-img.png";
import { RootState } from "../redux/store";
import ProductArtworkPreview from "../compoment/features/ProductArtworkPreview";
import LazyShimmerImage from "../compoment/ui/LazyShimmerImage";
import { toast } from "react-toastify";
import {
  isMultiStickerPackCartItem,
  isSingleStickerCartItem,
  isStickerSheetCartItem,
  merchCartLineMatches,
  resolveCartItemType,
} from "../utils/cartArtUtils";

interface MerchantiseItemModalProps extends ModalProps {
  onHide: () => void;
  data: Product | any;
  initialQuantity?: number;
  onQuantityChange?: (quantity: number) => void;
  onAddToCart?: () => void;
  favouriteImg?: string | null;
  /** Single Sticker generate mockup — used as order image for N-sticker packs. */
  singleStickerMockupUrl?: string | null;
}

const MerchantiseItemModal = (props: MerchantiseItemModalProps) => {
  const {
    data,
    show,
    onHide,
    initialQuantity = 0,
    onQuantityChange,
    onAddToCart,
    favouriteImg,
    singleStickerMockupUrl,
  } = props;
  const dispatch = useDispatch();
  const [successModal, setSuccessModal] = useState(false);
  const finalCanvasImages = useSelector(
    (s: RootState) => s.uploadFlow?.finalCanvasImages || {}
  );
  const cartItems = useSelector((s: RootState) => s.cart.items);
  const artSource = favouriteImg || null;

  const [count, setCount] = useState(initialQuantity);
  const [selectedSize, setSelectedSize] = useState<any>(null);
  const [selectedColor, setSelectedColor] = useState<any>(null);
  const [animate, setAnimate] = useState(false);

  const colorKey = getProductColorLabel(selectedColor) || "default";
  const previewItemKey = `${data?.sku_code?.toLowerCase() || data?.id?.toString() || "product"}-${colorKey}`;
  const finalCanvasImage =
    finalCanvasImages[previewItemKey] ||
    finalCanvasImages[data?.sku_code?.toLowerCase()] ||
    null;

  const findCartItemForSelection = (size: any, color: any) => {
    if (!data?.id) return undefined;
    const sizeLabel = size ? formatProductSizeWithInches(size) : undefined;
    const colorLabel = color ? getProductColorLabel(color) : undefined;
    return cartItems.find((item) =>
      merchCartLineMatches(item, data.id, {
        size: sizeLabel || undefined,
        variety: colorLabel || undefined,
        color: colorLabel || undefined,
        type: resolveCartItemType(data?.type),
      })
    );
  };

  useEffect(() => {
    if (show && data) {
      const sticker = productNameIncludesSticker(data?.name);
      const sizes = uniqueSortedProductSizes(data?.product_sizes);
      const colors = sticker ? [] : sortProductColorsLightFirst(data?.color);

      const existingForProduct = cartItems.find((item) =>
        merchCartLineMatches(item, data.id, {
          size: item.size,
          variety: item.variety || item.color,
          color: item.color || item.variety,
          type: resolveCartItemType(data?.type),
        })
      );

      const cartColor =
        (!sticker &&
          colors.find((color) => {
            const label = getProductColorLabel(color).toLowerCase();
            const existingLabel = String(
              existingForProduct?.color || existingForProduct?.variety || ""
            ).toLowerCase();
            return label && label === existingLabel;
          })) ||
        null;
      const preferredColor = findPreferredProductColor(colors, data);
      // Prefer cart / default color when in stock; otherwise first color that has stock.
      const matchedColor =
        (cartColor &&
        isVariantInStock(data, { colorLabel: getProductColorLabel(cartColor) })
          ? cartColor
          : null) ||
        (preferredColor &&
        isVariantInStock(data, {
          colorLabel: getProductColorLabel(preferredColor),
        })
          ? preferredColor
          : null) ||
        colors.find((color) =>
          isVariantInStock(data, { colorLabel: getProductColorLabel(color) })
        ) ||
        preferredColor ||
        colors[0] ||
        null;

      const matchedSize =
        sizes.find(
          (size) =>
            getProductSizeLabel(size).toLowerCase() ===
              String(existingForProduct?.size || "").toLowerCase() ||
            formatProductSizeWithInches(size).toLowerCase() ===
              String(existingForProduct?.size || "").toLowerCase()
        ) ||
        findPreferredProductSize(data, matchedColor) ||
        sizes[0] ||
        null;

      setSelectedSize(matchedSize);
      setSelectedColor(matchedColor);
      const colorLabel = matchedColor ? getProductColorLabel(matchedColor) : "";
      setCurrentColorImage(
        resolveProductColorImage(data, colorLabel) ||
          normalizeMediaUrl(matchedColor?.productImageUrl, data?.picture) ||
          resolveProductDisplayImage(data) ||
          PlaceholderImg
      );

      const matchedCartItem = findCartItemForSelection(matchedSize, matchedColor);
      const stock = getAvailableStock(data, {
        size: matchedSize,
        color: matchedColor,
      });
      const nextCount = matchedCartItem?.quantity ?? initialQuantity;
      setCount(stock > 0 ? Math.min(nextCount, stock) : Math.max(0, nextCount));
    }
  }, [show, data, initialQuantity, cartItems]);

  const triggerAnimation = () => {
    setAnimate(true);
    setTimeout(() => setAnimate(false), 200);
  };

  const increment = () => {
    setCount((c) => {
      const stock = getAvailableStock(data, {
        size: selectedSize,
        color: selectedColor,
      });
      if (stock > 0 && c >= stock) return c;
      triggerAnimation();
      const newCount = c + 1;
      onQuantityChange?.(newCount);
      return newCount;
    });
  };

  const decrement = () => {
    setCount((c) => {
      if (c <= 0) return c;
      triggerAnimation();
      const newCount = c - 1;
      onQuantityChange?.(newCount);
      return newCount;
    });
  };

  const selectedPrice =
    selectedSize?.price ||
    (typeof data?.price === "number" ? data.price : parseFloat(String(data?.price || "0")));
  const totalPrice = selectedPrice * count;

  const productName =
    typeof data?.name === "string"
      ? data.name
      : typeof data?.name === "object" && data.name?.name
        ? data.name.name
        : "Product";
  // Stickers expose product_sizes / inches; colors stay hidden.
  const isSticker = productNameIncludesSticker(productName);

  const productSizes = uniqueSortedProductSizes(data?.product_sizes);
  const validColors = isSticker ? [] : sortProductColorsLightFirst(data?.color);

  const baseProductImage = resolveProductDisplayImage(data) || PlaceholderImg;

  const selectedColorImage =
    selectedColor && typeof selectedColor === "object" && selectedColor.productImageUrl
      ? selectedColor.productImageUrl
      : null;

  const [currentColorImage, setCurrentColorImage] = useState<string>(baseProductImage);

  const normalizedName = productName;

  const variantStock = getAvailableStock(data, {
    size: selectedSize,
    color: selectedColor,
  });
  const variantInStock = isVariantInStock(data, {
    size: selectedSize,
    color: selectedColor,
  });
  const canIncrement = variantStock > 0 && count < variantStock;

  const handleAddtoCart = () => {
    if (count <= 0) return;
    if (validColors.length > 0 && !getProductColorLabel(selectedColor)) {
      toast.error("Please select a color");
      return;
    }
    if (productSizes.length > 0 && !selectedSize) {
      toast.error("Please select a size");
      return;
    }
    if (!variantInStock) {
      toast.error("This item is sold out");
      return;
    }
    if (variantStock > 0 && count > variantStock) {
      toast.error(`Only ${variantStock} in stock`);
      return;
    }

    const sizeLabel = selectedSize
      ? formatProductSizeWithInches(selectedSize) || undefined
      : undefined;

    const matchedSizeRow =
      data?.product_sizes &&
      Array.isArray(data.product_sizes) &&
      data.product_sizes.find((row: any) => {
        const rowSize =
          formatProductSizeWithInches(row) || getProductSizeLabel(row);
        const sizeOk =
          !sizeLabel ||
          rowSize.toLowerCase() === sizeLabel.toLowerCase() ||
          getProductSizeLabel(row).toLowerCase() ===
            getProductSizeLabel(selectedSize).toLowerCase();
        const rowColor = getProductColorLabel(row?.color).toLowerCase();
        const selectedColorLabel = getProductColorLabel(selectedColor).toLowerCase();
        const colorOk =
          !selectedColorLabel || !rowColor || rowColor === selectedColorLabel;
        return sizeOk && colorOk;
      });

    const mockupImage =
      selectedColorImage ||
      currentColorImage ||
      data?.picture ||
      data?.imgURL ||
      data?.image_url ||
      baseProductImage;

    const stickerIdentity = {
      name: normalizedName,
      sku_code: data?.sku_code,
    };
    const isMultiStickerPack = isMultiStickerPackCartItem(stickerIdentity);
    const isSingleSticker = isSingleStickerCartItem(stickerIdentity);
    const isSheetSticker = isStickerSheetCartItem(stickerIdentity);
    const usesGenerateMockupOnOrder =
      isMultiStickerPack || isSingleSticker || isSheetSticker;

    const apiSingleStickerUrl =
      (typeof data?.image_url_single_sticker === "string" &&
        data.image_url_single_sticker.trim()) ||
      "";
    const legacySingleStickerImage =
      (typeof singleStickerMockupUrl === "string" && singleStickerMockupUrl.trim()) ||
      "";
    const nPackOrderImage = apiSingleStickerUrl || legacySingleStickerImage;

    // Cart display: N-packs keep collage mockup in UI; order uses image_url_single_sticker.
    const displayMockupImage = mockupImage;

    // Orders: stickers → generate mockup; tote/apparel → session final art only.
    const sessionFinalArt =
      (typeof artSource === "string" && artSource.trim()) ||
      (typeof finalCanvasImage === "string" && finalCanvasImage.trim()) ||
      "";

    const cartItem = {
      id: data?.id || Date.now(),
      productId: data?.id || Date.now(),
      productSizeId: matchedSizeRow?.id || selectedSize?.id || undefined,
      name: normalizedName,
      type: resolveCartItemType(data?.type),
      quantity: count,
      price: selectedPrice,
      imageUrl: displayMockupImage,
      // Never store merch/generate mockup as finalCanvasImage (tote must keep session art).
      finalCanvasImage: sessionFinalArt || undefined,
      imgURL: displayMockupImage,
      ...(isMultiStickerPack && nPackOrderImage
        ? { image_url_single_sticker: nPackOrderImage }
        : {}),
      size: sizeLabel,
      dimensions: sizeLabel,
      inches: selectedSize ? getProductSizeInches(selectedSize) : undefined,
      variety: isSticker
        ? undefined
        : (selectedColor ? getProductColorLabel(selectedColor) || undefined : undefined),
      color: isSticker
        ? undefined
        : (selectedColor ? getProductColorLabel(selectedColor) || undefined : undefined),
      sku_code: data?.sku_code,
      merchGeneratedMockup: Boolean(
        usesGenerateMockupOnOrder ||
          data?.merchGeneratedMockup ||
          (selectedColor &&
            typeof selectedColor === "object" &&
            (selectedColor as { merchGeneratedMockup?: boolean }).merchGeneratedMockup)
      ),
      artworkPlacement:
        data?.artwork_placement || data?.artworkPlacement || data?.print_area,
    };

    dispatch(upsertCartItem(cartItem));
    setSuccessModal(true);
    onAddToCart?.();
    setTimeout(() => onHide(), 2000);
  };

  const renderImageMapping = () => {
    // Prefer live color from `data` so mockup URLs/flags stay in sync after generate.
    const selectedLabel = getProductColorLabel(selectedColor).toLowerCase();
    const liveSelectedColor =
      (selectedLabel &&
        validColors.find(
          (color) => getProductColorLabel(color).toLowerCase() === selectedLabel
        )) ||
      selectedColor;
    const liveColorImage =
      (liveSelectedColor &&
        typeof liveSelectedColor === "object" &&
        typeof liveSelectedColor.productImageUrl === "string" &&
        liveSelectedColor.productImageUrl.trim()) ||
      (selectedLabel ? resolveProductColorImage(data, selectedLabel) : null) ||
      "";
    const productImage =
      liveColorImage || currentColorImage || data?.picture || baseProductImage;
    // Only skip the art overlay when this color (or the product) has a real
    // generated mockup. Blank color photos (e.g. Black tote) must still composite art.
    const selectedColorHasGeneratedMockup =
      liveSelectedColor &&
      typeof liveSelectedColor === "object" &&
      Boolean(
        (liveSelectedColor as { merchGeneratedMockup?: boolean }).merchGeneratedMockup
      );
    const useGeneratedMockup =
      Boolean(productImage) &&
      (selectedColorHasGeneratedMockup ||
        (Boolean(data?.merchGeneratedMockup) && validColors.length === 0));

    if (useGeneratedMockup) {
      const mockupSrc =
        (selectedColorHasGeneratedMockup && productImage) ||
        baseProductImage ||
        productImage;
      return (
        <LazyShimmerImage
          key={`${previewItemKey}-generated-${mockupSrc}`}
          src={mockupSrc}
          alt={normalizedName}
          className="lazyShimmerImage--merchModal"
        />
      );
    }
    return (
      <ProductArtworkPreview
        key={previewItemKey}
        productImage={productImage}
        artworkImage={artSource}
        itemKey={previewItemKey}
        productName={normalizedName}
        skuCode={data?.sku_code}
        placement={data?.artwork_placement || data?.artworkPlacement || data?.print_area}
        lazy={false}
      />
    );
  };

  return (
    <>
      <Modal
        {...props}
        className="merchandiseModal"
        centered
        scrollable={false}
        show={show}
        onHide={onHide}
      >
        <Modal.Body className="w-100">
          <button type="button" className="closeBtn" onClick={onHide} aria-label="Close">
            <Close />
          </button>

          <Row>
            <Col md={4} className="d-flex align-items-center modalCanvas">
              {renderImageMapping() || (
                <img
                  src={selectedColorImage || baseProductImage}
                  alt={productName}
                  style={{ maxWidth: "100%", objectFit: "contain", borderRadius: 8 }}
                />
              )}
            </Col>

            <Col md={8}>
              <div className="detailMerchandise">
                <div className="heading">{productName}</div>

                {productSizes.length > 0 && (
                  <>
                    <div className="subHeading">Size</div>
                    <div className="sizeContainer">
                      {productSizes.map((size: any, i: number) => {
                        const label = formatProductSizeWithInches(size);
                        const isSelected = selectedSize?.id === size.id ||
                          formatProductSizeWithInches(selectedSize).toLowerCase() ===
                            label.toLowerCase() ||
                          getProductSizeLabel(selectedSize).toLowerCase() ===
                            getProductSizeLabel(size).toLowerCase();
                        const sizeInStock = isSticker
                          ? true
                          : isSizeAvailableForColor(data, size, selectedColor);
                        return (
                          <div
                            key={size.id || i}
                            className={`singleSize ${isSelected ? "selected" : ""}${
                              sizeInStock ? "" : " soldOut"
                            }`}
                            aria-disabled={!sizeInStock}
                            onClick={() => {
                              if (!sizeInStock) return;
                              setSelectedSize(size);
                              const matched = findCartItemForSelection(size, selectedColor);
                              const stock = getAvailableStock(data, {
                                size,
                                color: selectedColor,
                              });
                              const raw = matched?.quantity ?? 1;
                              const nextCount = isSticker
                                ? Math.max(raw, 1)
                                : stock > 0
                                  ? Math.min(Math.max(raw, 1), stock)
                                  : 0;
                              setCount(nextCount);
                              onQuantityChange?.(nextCount);
                            }}
                          >
                            {label}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {validColors.length > 0 && (
                  <>
                    <div className="subHeading">Color</div>
                    <div className="sizeContainer">
                      {validColors.map((color: any, i: number) => {
                        const label = getProductColorLabel(color);
                        if (!label) return null;
                        const selectedLabel = getProductColorLabel(selectedColor);
                        const isSelected =
                          selectedLabel.toLowerCase() === label.toLowerCase() ||
                          selectedColor === color;
                        const colorInStock = isColorAvailableForSize(
                          data,
                          color,
                          selectedSize
                        );

                        return (
                          <div
                            key={`${label}-${i}`}
                            className={`singleSize ${isSelected ? "selected" : ""}${
                              colorInStock ? "" : " soldOut"
                            }`}
                            onClick={() => {
                              setSelectedColor(color);
                              const nextLabel = getProductColorLabel(color);
                              setCurrentColorImage(
                                resolveProductColorImage(data, nextLabel) ||
                                  normalizeMediaUrl(color?.productImageUrl, data?.picture) ||
                                  baseProductImage
                              );
                              const nextSize =
                                selectedSize &&
                                isSizeAvailableForColor(data, selectedSize, color)
                                  ? selectedSize
                                  : findPreferredProductSize(data, color) || selectedSize;
                              if (nextSize !== selectedSize) {
                                setSelectedSize(nextSize);
                              }
                              const matched = findCartItemForSelection(nextSize, color);
                              const stock = getAvailableStock(data, {
                                size: nextSize,
                                color,
                              });
                              const raw = matched?.quantity ?? 1;
                              const nextCount =
                                stock > 0 ? Math.min(Math.max(raw, 1), stock) : 0;
                              setCount(nextCount);
                              onQuantityChange?.(nextCount);
                            }}
                          >
                            <span>{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {data?.brand_name && (
                  <>
                    <div className="subHeading">Phone Brand and Model</div>
                    <div className="position-relative">
                      <span className="searchIcon">
                        <Search size={20} />
                      </span>
                      <input
                        type="text"
                        className="brandField"
                        placeholder="Search your Phone brand and model"
                      />
                    </div>
                  </>
                )}

                <div className="subHeading">Quantity</div>
                <div className="quantitySection">
                  <div
                    className={`minus action ${count > 0 ? "active" : "disabled"}`}
                    onClick={decrement}
                    style={{ cursor: count > 0 ? "pointer" : "not-allowed" }}
                  >
                    <Minus size={22} />
                  </div>
                  <div className={`value ${animate ? "pop" : ""}`}>{count}</div>
                  <div
                    className={`plus action ${canIncrement ? "active" : "disabled"}`}
                    onClick={() => {
                      if (!canIncrement) return;
                      increment();
                    }}
                    style={{ cursor: canIncrement ? "pointer" : "not-allowed" }}
                  >
                    <Plus size={22} />
                  </div>
                </div>

                <div className="subHeading">Price</div>
                <div className="priceTxt">{formatPrice(totalPrice)}</div>

                {!isSticker ? (
                  <div
                    className={`stockStatusLine ${
                      variantStock > 0 ? "qtyOk" : "qtyDanger"
                    }`}
                  >
                    {variantStock > 0
                      ? `In stock: ${variantStock} left`
                      : "Sold out"}
                  </div>
                ) : null}

                <button className="btnCancel me-3" onClick={onHide}>
                  Cancel
                </button>
                <button
                  className="btnAddtoCart"
                  onClick={handleAddtoCart}
                  disabled={
                    count <= 0 ||
                    !variantInStock ||
                    (validColors.length > 0 && !getProductColorLabel(selectedColor)) ||
                    (productSizes.length > 0 && !selectedSize)
                  }
                  style={{
                    opacity:
                      count <= 0 ||
                        !variantInStock ||
                        (validColors.length > 0 && !getProductColorLabel(selectedColor)) ||
                        (productSizes.length > 0 && !selectedSize)
                        ? 0.65
                        : 1,
                    cursor:
                      count <= 0 ||
                        !variantInStock ||
                        (validColors.length > 0 && !getProductColorLabel(selectedColor)) ||
                        (productSizes.length > 0 && !selectedSize)
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <span className="me-1">
                    <AddCart />
                  </span>{" "}
                  {isSticker || variantInStock ? "Add to Cart" : "Sold Out"}
                </button>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <AddtoCart show={successModal} onHide={() => setSuccessModal(false)} />
    </>
  );
};

export default MerchantiseItemModal;